import type { Cell, Constraint, ConstraintType, Difficulty, Puzzle, Restriction, Tile } from './types';
import type { Axis } from './constants';
import { cellCountForSize, getAnchorCandidates, getLines } from './constants';
import { applyModifier, isPrime } from './rules';

// ─────────────────────────────────────────────────────────────────────
// Seedable PRNG
// ─────────────────────────────────────────────────────────────────────
// cyrb53: fast, well-distributed string hash. Used to turn a seed string
// like "hexponents-2026-05-05-Easy" into a 32-bit number for mulberry32.
function cyrb53(str: string, seed = 0): number {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

function mulberry32(seed: number): () => number {
  return function () {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = seed;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

class Rng {
  private rand: () => number;
  constructor(seed: string | number) {
    const n = typeof seed === 'string' ? cyrb53(seed) : seed;
    this.rand = mulberry32(n >>> 0);
  }
  next(): number { return this.rand(); }
  int(min: number, max: number): number {
    return Math.floor(this.rand() * (max - min + 1)) + min;
  }
  choice<T>(arr: readonly T[]): T {
    return arr[Math.floor(this.rand() * arr.length)];
  }
  shuffle<T>(arr: readonly T[]): T[] {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(this.rand() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }
  pickN<T>(arr: readonly T[], n: number): T[] {
    return this.shuffle(arr).slice(0, Math.min(n, arr.length));
  }
}

// ─────────────────────────────────────────────────────────────────────
// Difficulty parameters
// ─────────────────────────────────────────────────────────────────────
interface DifficultyParams {
  size?: number; // Hex grid side length; defaults to 3 (19 cells).
  numLocks: number;
  numModifiers: number;
  numRestrictions: number;
  numGaps?: number;
  minValue: number;
  maxValue: number;
  numConstraints: number;
  constraintTypes: { type: ConstraintType; weight: number }[];
  modifierOptions: string[];
  restrictionOptions: Restriction[];
}

const PARAMS: Record<Difficulty, DifficultyParams> = {
  // Easy is the on-ramp — 7 cells (side-2 grid), nearly half pre-locked,
  // sum-only constraints, all-positive small numbers. The intent is "a
  // quick dopamine hit before the real puzzles" rather than a real
  // logic challenge — Hard + Expert are where the difficulty lives.
  Easy: {
    size: 2,
    numLocks: 3,
    numModifiers: 0,
    numRestrictions: 0,
    minValue: 1, maxValue: 5,
    numConstraints: 4,
    constraintTypes: [{ type: 'sum', weight: 1 }],
    modifierOptions: [],
    restrictionOptions: [],
  },
  // Medium is a step-up but still squarely in the "warm-up" tier —
  // tuned ~25% easier than the previous Medium so it bridges Easy →
  // Hard without a difficulty cliff. Standard size-3 grid (19 cells)
  // so players see the layout they'll encounter on Hard.
  Medium: {
    numLocks: 4,
    numModifiers: 1,
    numRestrictions: 2,
    minValue: -2, maxValue: 6,
    numConstraints: 5,
    constraintTypes: [
      { type: 'sum', weight: 5 },
      { type: 'noNeg', weight: 2 },
    ],
    modifierOptions: ['+2', '-1'],
    restrictionOptions: ['even', 'odd', 'pos'],
  },
  Hard: {
    numLocks: 4,
    numModifiers: 2,
    numRestrictions: 5,
    numGaps: 2,
    minValue: -5, maxValue: 9,
    numConstraints: 9,
    constraintTypes: [
      { type: 'sum', weight: 6 },
      { type: 'noNeg', weight: 2 },
      { type: 'negCount', weight: 1 },
      { type: 'product', weight: 1 },
    ],
    modifierOptions: ['×2', '+5', '-3', '×-1', '²', '³', '÷2', '÷3'],
    restrictionOptions: ['even', 'odd', 'pos', 'neg', 'prime', 'magHi', 'magLo'],
  },
  // Expert is a premium tier — a side-4 hex grid (37 cells, 7 lines per axis),
  // every modifier type, and the full restriction palette.
  Expert: {
    size: 4,
    numLocks: 6,
    numModifiers: 5,
    numRestrictions: 8,
    numGaps: 4,
    minValue: -6, maxValue: 10,
    numConstraints: 16,
    constraintTypes: [
      { type: 'sum',      weight: 5 },
      { type: 'product',  weight: 2 },
      { type: 'sumGte',   weight: 1 },
      { type: 'sumLte',   weight: 1 },
      { type: 'noNeg',    weight: 2 },
      { type: 'negCount', weight: 1 },
      { type: 'allEven',  weight: 1 },
      { type: 'allOdd',   weight: 1 },
    ],
    modifierOptions: ['×2', '+5', '-3', '×-1', '²', '³', '÷2', '÷3'],
    restrictionOptions: ['even', 'odd', 'pos', 'neg', 'prime', 'magHi', 'magLo'],
  },
};

// Anchor candidates per puzzle are derived from getAnchorCandidates(size).

const PUZZLE_NAMES = [
  'First Light', 'Second Wind', 'High Cipher', 'Bright Mark', 'Quiet Sum',
  'Late Knot', 'Soft Edge', 'Open Field', 'Long Trace', 'Cinder',
  'Fold', 'Lattice', 'Tessera', 'Vesper', 'Plait',
  'Trine', 'Hexis', 'Cipher Six', 'Ember', 'North Star',
];

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────
function pickWeighted<T>(rng: Rng, items: { type: T; weight: number }[]): T {
  const total = items.reduce((s, x) => s + x.weight, 0);
  let r = rng.next() * total;
  for (const it of items) {
    r -= it.weight;
    if (r <= 0) return it.type;
  }
  return items[items.length - 1].type;
}

function valueValidForRestriction(v: number, r: Restriction): boolean {
  switch (r) {
    case 'even':  return v % 2 === 0;
    case 'odd':   return Math.abs(v % 2) === 1;
    case 'pos':   return v >= 1;
    case 'neg':   return v <= -1;
    case 'prime': return isPrime(v);
    case 'magHi': return Math.abs(v) >= 5;
    case 'magLo': return Math.abs(v) <= 4;
  }
}

// Sample an integer in range that satisfies the restriction; falls back
// to a scan if the tight RNG loop exhausts.
function sampleValueForRestriction(rng: Rng, r: Restriction, minV: number, maxV: number): number {
  for (let i = 0; i < 50; i++) {
    const v = rng.int(minV, maxV);
    if (valueValidForRestriction(v, r)) return v;
  }
  for (let v = minV; v <= maxV; v++) {
    if (valueValidForRestriction(v, r)) return v;
  }
  return minV;
}

// For a modifier cell (and optional restriction): pick a tile value such
// that (a) applyModifier(tile, mod) sits in a sensible range, and (b) the
// resulting effective value satisfies the restriction (if any).
function sampleTileForModifier(
  rng: Rng,
  modifier: string,
  restriction: Restriction | undefined,
  minV: number, maxV: number,
): { tileValue: number; effective: number } {
  for (let i = 0; i < 200; i++) {
    const tile = rng.int(minV, maxV);
    const effRaw = applyModifier(tile, modifier);
    if (effRaw == null) continue;
    const eff = effRaw;
    // keep effective values in a slightly wider range than tile range,
    // so modifiers can produce e.g. 2*6=12 without screaming
    if (eff < minV - 5 || eff > maxV + 15) continue;
    if (restriction && !valueValidForRestriction(eff, restriction)) continue;
    return { tileValue: tile, effective: eff };
  }
  // Last-ditch fallback: just use a tile=minV.
  const eff = applyModifier(minV, modifier) ?? minV;
  return { tileValue: minV, effective: eff };
}

// ─────────────────────────────────────────────────────────────────────
// Main generator
// ─────────────────────────────────────────────────────────────────────
// Deterministic from the seed string. Same seed → same puzzle.
// Solvability is guaranteed by construction: we sample a ground-truth
// solution first, then derive constraints from it.
export function generatePuzzle(seed: string, difficulty: Difficulty): Puzzle {
  const rng = new Rng(seed);
  const params = PARAMS[difficulty];

  const size = params.size ?? 3;
  const cellCount = cellCountForSize(size);
  const lines = getLines(size);
  const anchorCandidates = getAnchorCandidates(size);
  const allCellIndices = Array.from({ length: cellCount }, (_, i) => i);

  // 1) Pick gap positions first — holes in the grid that no tile occupies and
  //    no constraint can include. The configured `numGaps` is a MAX; the
  //    actual count is rolled so some days have no gaps at all and the
  //    puzzle layout doesn't always look the same.
  const maxGaps = params.numGaps ?? 0;
  const numGaps = maxGaps > 0 ? rng.int(0, maxGaps) : 0;
  const gapCandidates = allCellIndices.filter(i => !anchorCandidates.slice(0, 4).includes(i));
  const gapPositions = numGaps > 0 ? rng.pickN(gapCandidates, numGaps) : [];

  // 2) Pick lock positions from anchor candidates that aren't gaps.
  const lockPositions = rng.pickN(
    anchorCandidates.filter(i => !gapPositions.includes(i)),
    params.numLocks,
  );

  // 3) From the remaining cells, pick modifier slots, then restriction slots.
  const remaining = allCellIndices.filter(
    i => !lockPositions.includes(i) && !gapPositions.includes(i),
  );
  const shuffledRemaining = rng.shuffle(remaining);
  const modPositions = shuffledRemaining.slice(0, params.numModifiers);
  const restrPositions = shuffledRemaining.slice(
    params.numModifiers,
    params.numModifiers + params.numRestrictions,
  );

  // 4) Construct cells + ground-truth effective values per cell.
  const cells: Cell[] = Array.from({ length: cellCount }, () => ({ kind: 'empty' as const }));
  const effective: number[] = new Array(cellCount).fill(0);
  // tileValueByCell[i] = the tile value that ought to land in cell i.
  // Only populated for non-lock, non-gap cells (which become bag tiles).
  const tileValueByCell: Record<number, number> = {};

  for (const i of gapPositions) {
    cells[i] = { kind: 'gap' };
  }

  for (const i of lockPositions) {
    const v = rng.int(params.minValue, params.maxValue);
    cells[i] = { kind: 'locked', value: v };
    effective[i] = v;
  }

  for (const i of modPositions) {
    const mod = rng.choice(params.modifierOptions);
    const { tileValue, effective: eff } = sampleTileForModifier(
      rng, mod, undefined, params.minValue, params.maxValue,
    );
    cells[i] = { kind: 'empty', modifier: mod };
    tileValueByCell[i] = tileValue;
    effective[i] = eff;
  }

  for (const i of restrPositions) {
    const r = rng.choice(params.restrictionOptions);
    const v = sampleValueForRestriction(rng, r, params.minValue, params.maxValue);
    cells[i] = { kind: 'empty', _restriction: r };
    tileValueByCell[i] = v;
    effective[i] = v;
  }

  const plainEmpty = remaining.filter(
    i => !modPositions.includes(i) && !restrPositions.includes(i),
  );
  for (const i of plainEmpty) {
    const v = rng.int(params.minValue, params.maxValue);
    cells[i] = { kind: 'empty' };
    tileValueByCell[i] = v;
    effective[i] = v;
  }

  // 5) Build bag from the non-lock, non-gap cells (one tile per such cell).
  const bag: Tile[] = remaining.map((i, idx) => ({
    id: `t${idx}`,
    value: tileValueByCell[i],
  }));

  // 6) Pick lines and assign constraints. Constraint values are computed
  //    from the ground-truth effective values of the non-gap cells, so the
  //    puzzle is always solvable (at least by the generated solution).
  const numLinesPerAxis = lines.col.length;
  const allLines: { axis: Axis; index: number }[] = [];
  for (const axis of ['col', 'nwse', 'nesw'] as Axis[]) {
    for (let li = 0; li < numLinesPerAxis; li++) allLines.push({ axis, index: li });
  }
  // Skip lines whose non-gap cells are too few to make a meaningful constraint.
  const linesWithRealCells = allLines.filter(({ axis, index }) =>
    lines[axis][index].filter(i => cells[i].kind !== 'gap').length >= 2
  );
  const pickedLines = rng.shuffle(linesWithRealCells).slice(0, params.numConstraints);

  // For a given line's ground-truth values, certain constraint types are
  // unsatisfiable (e.g. `noNeg` on a line containing negatives). Drop those
  // candidates before sampling so every emitted constraint matches reality.
  const isTypeValidForLine = (type: ConstraintType, vals: number[]): boolean => {
    switch (type) {
      case 'noNeg':   return vals.every(x => x >= 0);
      case 'allNeg':  return vals.every(x => x < 0);
      case 'allEven': return vals.every(x => x % 2 === 0);
      case 'allOdd':  return vals.every(x => Math.abs(x % 2) === 1);
      default:        return true;
    }
  };

  const constraints: Constraint[] = pickedLines.map(({ axis, index }) => {
    const lineCellIdxs = lines[axis][index].filter(i => cells[i].kind !== 'gap');
    const vals = lineCellIdxs.map(i => effective[i]);
    const validTypes = params.constraintTypes.filter(({ type }) => isTypeValidForLine(type, vals));
    // sum is always valid, so the fallback list is non-empty.
    const pool = validTypes.length > 0 ? validTypes : [{ type: 'sum' as ConstraintType, weight: 1 }];
    const type = pickWeighted(rng, pool);
    let value: number | undefined;
    switch (type) {
      case 'sum':      value = vals.reduce((a, b) => a + b, 0); break;
      case 'product':  value = vals.reduce((a, b) => a * b, 1); break;
      case 'sumGte':   value = vals.reduce((a, b) => a + b, 0); break;
      case 'sumLte':   value = vals.reduce((a, b) => a + b, 0); break;
      case 'negCount': value = vals.filter(x => x < 0).length; break;
      // allEven / allOdd / noNeg / allNeg / contains have no value
    }
    return value !== undefined
      ? { axis, index, type, value }
      : { axis, index, type };
  });

  // 6) Pick a name. Same seed → same name.
  const nameIdx = (cyrb53(seed, 1) >>> 0) % PUZZLE_NAMES.length;
  const name = PUZZLE_NAMES[nameIdx];

  return { difficulty, name, cells, bag, constraints };
}

// ─────────────────────────────────────────────────────────────────────
// Daily seed (Eastern timezone rollover)
// ─────────────────────────────────────────────────────────────────────
// "Today" in America/New_York. Used so a new puzzle appears at midnight
// US Eastern. en-CA gives YYYY-MM-DD ISO-like dates.
export function dailyDateString(d: Date = new Date()): string {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit',
  });
  return fmt.format(d);
}

export function dailySeed(difficulty: Difficulty, d: Date = new Date()): string {
  return `hexponents-${dailyDateString(d)}-${difficulty}`;
}

export function dailyPuzzle(difficulty: Difficulty, d: Date = new Date()): Puzzle {
  return generatePuzzle(dailySeed(difficulty, d), difficulty);
}
